import React from "react";
import {
  Sparkles,
  Wand2,
  Zap,
  Compass,
  BarChart3,
  AlarmClock,
} from "lucide-react";
import BgOrbs from "./BgOrbs";

const BENEFITS = [
  {
    icon: Sparkles,
    title: "Use existing content",
    body: "No rebuild required. Point AI Trainer at what you already have.",
    accent: "from-blue-500 to-indigo-500",
  },
  {
    icon: Wand2,
    title: "Create AI sessions automatically",
    body: "AI structures your content into guided, ready-to-run sessions.",
    accent: "from-violet-500 to-fuchsia-500",
  },
  {
    icon: Zap,
    title: "Deliver consistent training",
    body: "Every learner gets the same polished, on-brand experience.",
    accent: "from-amber-500 to-orange-500",
  },
  {
    icon: Compass,
    title: "Guide learners with AI",
    body: "A non-human AI tutor that teaches, explains, and paces properly.",
    accent: "from-teal-500 to-emerald-500",
  },
  {
    icon: BarChart3,
    title: "Measure understanding clearly",
    body: "See completion, engagement, and topic-level weak spots.",
    accent: "from-sky-500 to-blue-500",
  },
  {
    icon: AlarmClock,
    title: "Intervene at the right moment",
    body: "Surface who needs help and when a human trainer should step in.",
    accent: "from-rose-500 to-pink-500",
  },
];

export default function Benefits() {
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="relative min-h-screen flex items-center py-20 bg-white overflow-hidden"
    >
      <BgOrbs variant="blue" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
            Outcomes
          </div>
          <h2 className="mt-3 text-[34px] sm:text-[44px] leading-[1.08] font-extrabold tracking-tight text-slate-900 text-balance">
            What teams get with AI Trainer
          </h2>
          <p className="mt-4 text-[16.5px] text-slate-600 leading-relaxed">
            A faster path from content to capability.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                className="card-lift relative group bg-gradient-to-br from-slate-50 to-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 hover:border-blue-200 hover:shadow-[0_16px_40px_-20px_rgba(37,99,235,0.25)]"
                data-testid={`benefit-card-${i}`}
              >
                <div
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${b.accent} text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]`}
                >
                  <Icon size={20} strokeWidth={2} />
                </div>
                <h3 className="mt-4 text-[17px] font-bold tracking-tight text-slate-900">
                  {b.title}
                </h3>
                <p className="mt-1.5 text-[14px] text-slate-600 leading-relaxed">
                  {b.body}
                </p>
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                  style={{
                    background:
                      "radial-gradient(200px 100px at 30% 0%, rgba(59,130,246,0.08), transparent 60%)",
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
